/**
 * Alpine.js Trip Editor Component
 * Unified component for trip creation (modal) and inline editing (detail page)
 */

import { createDatePickerMixin } from './trip-date-picker.js';
import { initCityAutocomplete } from './city-autocomplete.js';
import { formatFutureRelative } from './time-utils.js';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

// Helper to get translation (falls back to key if i18n not available)
function t(key) {
    return window.t ? window.t(key) : key;
}

const VIEW_ACL_VALUES = ['0', '1', '2', '3'];

// Debounce helper for auto-save
function debounce(fn, delay) {
    let timeoutId;
    return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
}

/**
 * Trip Editor Component Factory
 * Handles both modal and inline editing modes
 *
 * Usage in Twig:
 *   Modal: x-data="tripEditor()" with @open-trip-modal.window
 *   Inline: x-data="tripEditor({ mode: 'inline', tripData: {{ trip|json_encode }} })"
 */
export function tripEditor(config = {}) {
    return {
        // Spread date picker mixin
        ...createDatePickerMixin(),

        // Mode: 'modal' | 'inline' | 'progressive'
        // progressive = always-editable inline fields with auto-save
        mode: config.mode || 'modal',

        // Modal-specific state
        isOpen: false,

        // Inline-specific state
        isEditing: false,

        // Progressive mode state
        isEditingDestination: false, // Only destination/dates need explicit edit mode
        autoSaveStatus: '', // '', 'saving', 'saved', 'error'
        autoSaveTimeout: null,

        // Shared form state
        tripId: null,
        ownerUsername: null,
        city: { id: null, name: '', countryIso: '', countryName: '' },
        citySearch: '',
        startDate: '',
        endDate: '',
        description: '',
        lookingFor: [],
        tripType: null,
        viewAcl: '0',
        isSaving: false,

        // Original data for reset (inline editing)
        originalData: null,

        // Computed properties
        get hasDates() {
            return this.startDate && this.endDate;
        },

        get dateDisplay() {
            void this.$store?.i18n?.ready;
            if (!this.startDate) return t('common.post_trip_when_button');
            if (!this.endDate) return this.formatDateShort(this.startDate) + ' - ?';
            return this.formatDateShort(this.startDate) + ' - ' + this.formatDateShort(this.endDate);
        },

        get canSave() {
            return this.city.id && this.startDate && this.endDate;
        },

        get viewAclLabel() {
            void this.$store?.i18n?.ready;
            const labels = {
                '0': t('common.post_visibility_all_users'),
                '1': t('common.post_visibility_members'),
                '2': t('common.post_visibility_my_friends'),
                '3': t('common.post_visibility_only_me')
            };
            return labels[this.viewAcl] || t('common.post_visibility_all_users');
        },

        /**
         * Get relative time label for the trip start date
         */
        get relativeTimeLabel() {
            if (!this.startDate) return '';
            return formatFutureRelative(this.startDate);
        },

        get isPastTrip() {
            if (!this.endDate) return false;
            const end = new Date(this.endDate + 'T23:59:59');
            return end < new Date();
        },

        /**
         * Format date range for display (e.g., "January 21 - 23, 2026" or "January 21 - February 5, 2026")
         * Uses localized month names when available
         */
        formatDateRange(startDate, endDate) {
            if (!startDate || !endDate) return '';
            const start = new Date(startDate + 'T00:00:00');
            const end = new Date(endDate + 'T00:00:00');

            // Use localized month names if available, otherwise fallback
            const getMonth = (idx) => {
                if (window.getLocalizedMonthFull) {
                    return window.getLocalizedMonthFull(idx);
                }
                const fallback = ['January', 'February', 'March', 'April', 'May', 'June',
                                  'July', 'August', 'September', 'October', 'November', 'December'];
                return fallback[idx];
            };

            const startMonth = getMonth(start.getMonth());
            const endMonth = getMonth(end.getMonth());
            const startDay = start.getDate();
            const endDay = end.getDate();
            const endYear = end.getFullYear();

            if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
                return `${startMonth} ${startDay} - ${endDay}, ${endYear}`;
            }
            return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`;
        },

        /**
         * Initialize component (called automatically by Alpine)
         */
        init() {
            // Load initial trip data if provided in config
            if (config.tripData) {
                this.loadTripData(config.tripData);
                this.originalData = { ...config.tripData };
            }
        },

        /**
         * Load trip data into form fields
         * @param {Object} tripData
         */
        loadTripData(tripData) {
            this.tripId = tripData.id || null;
            this.ownerUsername = tripData.owner_username || null;
            this.city = {
                id: tripData.city_id || null,
                name: tripData.city_name || '',
                countryIso: (tripData.country_code || '').toLowerCase(),
                countryName: tripData.country_name || ''
            };
            this.citySearch = tripData.city_name || '';
            this.startDate = tripData.start_date || '';
            this.endDate = tripData.end_date || '';
            this.description = tripData.description || '';
            this.lookingFor = tripData.looking_for || [];
            this.tripType = tripData.trip_type || null;
            this.viewAcl = String(tripData.view_acl || 0);
        },

        // === Modal Methods ===

        /**
         * Open modal (for modal mode)
         * @param {Object|null} tripData - Trip data for editing, null for new trip
         */
        open(tripData = null) {
            if (tripData && tripData.id) {
                this.loadTripData(tripData);
            } else {
                this.reset();
            }
            this.isOpen = true;
            lockBodyScroll();
            this.$nextTick(() => {
                this.initCityAutocomplete();
                if (!this.tripId) {
                    this.$refs.cityInput?.focus();
                }
                window.LucideIcons?.initialize(this.$el);
            });
        },

        /**
         * Close modal (for modal mode)
         */
        close() {
            this.isOpen = false;
            this.showDatePicker = false;
            unlockBodyScroll();
        },

        /**
         * Open modal and immediately trigger delete confirmation (for modal mode)
         * Used by trip-card.twig delete button
         * @param {Object} tripData - Trip data to delete
         */
        openAndDelete(tripData) {
            if (tripData && tripData.id) {
                this.loadTripData(tripData);
                this.confirmDelete();
            }
        },

        // === Inline Methods ===

        /**
         * Start inline editing (for inline mode)
         */
        startEdit() {
            this.isEditing = true;
            this.$nextTick(() => {
                this.initCityAutocomplete();
            });
        },

        /**
         * Cancel inline editing and restore original values (for inline mode)
         */
        cancelEdit() {
            if (this.originalData) {
                this.loadTripData(this.originalData);
            }
            this.isEditing = false;
            this.showDatePicker = false;
        },

        // === Shared Methods ===

        /**
         * Reset form to initial state (for creating new trip)
         */
        reset() {
            this.tripId = null;
            this.ownerUsername = null;
            this.city = { id: null, name: '', countryIso: '', countryName: '' };
            this.citySearch = '';
            this.startDate = '';
            this.endDate = '';
            this.description = '';
            this.lookingFor = [];
            this.tripType = null;
            this.viewAcl = '0';
            this.resetDatePicker();
        },

        /**
         * Initialize city autocomplete on the input element
         */
        initCityAutocomplete() {
            const input = this.$refs.cityInput;
            initCityAutocomplete(input, (cityData) => {
                this.city = {
                    id: cityData.id,
                    name: cityData.name,
                    countryIso: cityData.countryIso,
                    countryName: ''
                };
                this.citySearch = cityData.name;
            });

            // Select all text on focus so user can immediately type to replace
            input.addEventListener('focus', () => {
                // Small delay to ensure the focus has completed
                setTimeout(() => input.select(), 0);
            });

            // Keep citySearch in sync with the actual input value as the user types
            // This prevents stale values when Alpine re-renders
            input.addEventListener('input', () => {
                this.citySearch = input.value;
                // Clear city.id when user manually edits the input (they must re-select from autocomplete)
                if (this.city.id && input.value !== this.city.name) {
                    this.city = { id: null, name: '', countryIso: '', countryName: '' };
                }
            });
        },

        /**
         * Toggle a "looking for" tag
         * @param {string} tag - Tag name (travel-buddy, local-tips, meetup)
         */
        toggleLookingFor(tag) {
            const idx = this.lookingFor.indexOf(tag);
            if (idx === -1) {
                this.lookingFor.push(tag);
            } else {
                this.lookingFor.splice(idx, 1);
            }
            // Auto-save in progressive mode
            if (this.mode === 'progressive') {
                this.autoSave();
            }
        },

        /**
         * Select a trip type (single select)
         * @param {string|null} type - Trip type (vacation, business, etc.) or null to clear
         */
        selectTripType(type) {
            this.tripType = type;
            // Auto-save in progressive mode
            if (this.mode === 'progressive') {
                this.autoSave();
            }
        },

        /**
         * Cycle through view ACL options
         */
        cycleViewAcl() {
            const currentIndex = VIEW_ACL_VALUES.indexOf(this.viewAcl);
            this.viewAcl = VIEW_ACL_VALUES[(currentIndex + 1) % VIEW_ACL_VALUES.length];
            // Auto-save in progressive mode
            if (this.mode === 'progressive') {
                this.autoSave();
            }
        },

        // === Progressive Mode Methods ===

        /**
         * Auto-save with debounce (for progressive mode)
         * Called when description, looking-for, trip type, or visibility changes
         */
        autoSave: debounce(async function() {
            if (!this.tripId || !this.canSave) return;

            this.autoSaveStatus = 'saving';
            try {
                const token = await window.getApiToken();
                const data = {
                    city_id: this.city.id,
                    start_date: this.startDate,
                    end_date: this.endDate,
                    description: this.description,
                    view_acl: this.viewAcl,
                    looking_for: JSON.stringify(this.lookingFor),
                    trip_type: this.tripType || ''
                };

                const response = await fetch(`/v1/trip/${this.tripId}`, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                    },
                    body: new URLSearchParams(data),
                    credentials: 'include'
                });

                if (response.ok) {
                    this.autoSaveStatus = 'saved';
                    // Update original data
                    this.originalData = {
                        ...this.originalData,
                        description: this.description,
                        looking_for: [...this.lookingFor],
                        trip_type: this.tripType,
                        view_acl: this.viewAcl
                    };
                    // Clear status after 2 seconds
                    setTimeout(() => {
                        if (this.autoSaveStatus === 'saved') {
                            this.autoSaveStatus = '';
                        }
                    }, 2000);
                } else {
                    this.autoSaveStatus = 'error';
                }
            } catch (e) {
                console.error('Auto-save error:', e);
                this.autoSaveStatus = 'error';
            }
        }, 800),

        /**
         * Handle description blur - trigger auto-save in progressive mode
         */
        onDescriptionBlur() {
            if (this.mode === 'progressive' && this.description !== this.originalData?.description) {
                this.autoSave();
            }
        },

        /**
         * Open destination/date editor (progressive mode)
         */
        openDestinationEditor() {
            this.isEditingDestination = true;
            this.$nextTick(() => {
                this.initCityAutocomplete();
            });
        },

        /**
         * Close destination/date editor and save (progressive mode)
         */
        async saveDestination() {
            if (!this.canSave) return;

            this.isSaving = true;
            try {
                const token = await window.getApiToken();
                const data = {
                    city_id: this.city.id,
                    start_date: this.startDate,
                    end_date: this.endDate,
                    description: this.description,
                    view_acl: this.viewAcl,
                    looking_for: JSON.stringify(this.lookingFor),
                    trip_type: this.tripType || ''
                };

                const response = await fetch(`/v1/trip/${this.tripId}`, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                    },
                    body: new URLSearchParams(data),
                    credentials: 'include'
                });

                if (response.ok) {
                    // Update original data with new destination/dates
                    this.originalData = {
                        ...this.originalData,
                        city_id: this.city.id,
                        city_name: this.city.name,
                        country_code: this.city.countryIso?.toUpperCase(),
                        start_date: this.startDate,
                        end_date: this.endDate
                    };
                    this.isEditingDestination = false;
                    this.showDatePicker = false;
                    // Reload to update map coordinates if city changed
                    if (this.city.id !== config.tripData?.city_id) {
                        window.location.hash = '#trips';
                        window.location.reload();
                    }
                } else {
                    this.showError(t('common.trip_changes_save_error'));
                }
            } catch (e) {
                console.error('Save destination error:', e);
                this.showError(t('common.trip_changes_save_error'));
            }
            this.isSaving = false;
        },

        /**
         * Cancel destination/date editing (progressive mode)
         */
        cancelDestinationEdit() {
            if (this.originalData) {
                this.city = {
                    id: this.originalData.city_id || null,
                    name: this.originalData.city_name || '',
                    countryIso: (this.originalData.country_code || '').toLowerCase(),
                    countryName: this.originalData.country_name || ''
                };
                this.citySearch = this.originalData.city_name || '';
                this.startDate = this.originalData.start_date || '';
                this.endDate = this.originalData.end_date || '';
            }
            this.isEditingDestination = false;
            this.showDatePicker = false;
        },

        /**
         * Save trip (create or update)
         */
        async save() {
            if (!this.canSave || this.isSaving) return;

            this.isSaving = true;
            try {
                const token = await window.getApiToken();
                const url = this.tripId ? `/v1/trip/${this.tripId}` : '/v1/trip';
                const method = this.tripId ? 'PUT' : 'POST';

                const data = {
                    city_id: this.city.id,
                    start_date: this.startDate,
                    end_date: this.endDate,
                    description: this.description,
                    view_acl: this.viewAcl,
                    looking_for: JSON.stringify(this.lookingFor),
                    trip_type: this.tripType || ''
                };

                const response = await fetch(url, {
                    method: method,
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                    },
                    body: new URLSearchParams(data),
                    credentials: 'include'
                });

                if (response.ok) {
                    const tripData = await response.json();

                    if (this.mode === 'modal') {
                        this.close();
                        // Emit event for trips list to update
                        window.dispatchEvent(new CustomEvent('trip-saved', { detail: tripData }));
                        // Reload and scroll to trips section
                        window.location.hash = '#trips';
                        window.location.reload();
                    } else {
                        // Inline mode: keep current form values (they are already correct)
                        // and update originalData so cancel works correctly
                        this.originalData = {
                            id: this.tripId,
                            city_id: this.city.id,
                            city_name: this.city.name,
                            country_code: this.city.countryIso?.toUpperCase(),
                            country_name: this.city.countryName,
                            start_date: this.startDate,
                            end_date: this.endDate,
                            description: this.description,
                            looking_for: [...this.lookingFor],
                            trip_type: this.tripType,
                            view_acl: this.viewAcl
                        };
                        this.isEditing = false;
                        // Emit event for any listeners
                        window.dispatchEvent(new CustomEvent('trip-saved', { detail: tripData }));
                    }
                } else {
                    const data = await response.json();
                    this.showError(data.error?.message || t('common.trip_save_error'));
                }
            } catch (e) {
                console.error('Save error:', e);
                this.showError(t('common.trip_save_error'));
            }
            this.isSaving = false;
        },

        /**
         * Show delete confirmation dialog
         */
        confirmDelete() {
            if (!this.tripId) return;

            if (window.ConfirmModal) {
                window.ConfirmModal.show({
                    title: t('common.trip_delete_title'),
                    message: t('common.trip_delete_message'),
                    confirmText: t('common.trip_delete_button'),
                    type: 'danger',
                    onConfirm: () => this.doDelete()
                });
            } else {
                // Fallback to native confirm
                if (confirm(t('common.trip_delete_confirm'))) {
                    this.doDelete();
                }
            }
        },

        /**
         * Execute trip deletion
         */
        async doDelete() {
            this.isSaving = true;
            try {
                const token = await window.getApiToken();
                const response = await fetch(`/v1/trip/${this.tripId}`, {
                    method: 'DELETE',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    if (this.mode === 'modal') {
                        this.close();
                        // Remove the trip card from DOM or reload the page
                        const tripCard = document.querySelector(`[data-trip-id="${this.tripId}"]`);
                        if (tripCard) {
                            tripCard.remove();
                        }
                        window.dispatchEvent(new CustomEvent('trip-deleted', {
                            detail: { id: this.tripId }
                        }));
                    } else {
                        // Inline mode on detail page: navigate back to owner's trips
                        const tripsUrl = this.ownerUsername
                            ? `/app/trips?u=${encodeURIComponent(this.ownerUsername)}`
                            : '/app/trips';
                        window.location.href = tripsUrl;
                    }
                } else {
                    this.showError(t('common.trip_delete_error'));
                }
            } catch (e) {
                console.error('Delete error:', e);
                this.showError(t('common.trip_delete_error'));
            }
            this.isSaving = false;
        },

        /**
         * Show error message to user
         * @param {string} message
         */
        showError(message) {
            if (window.ConfirmModal) {
                window.ConfirmModal.show({
                    title: t('common.error_title'),
                    message: message,
                    confirmText: t('common.ok'),
                    type: 'info',
                    showCancel: false
                });
            } else {
                alert(message);
            }
        }
    };
}

// Register with Alpine when available
if (window.Alpine) {
    window.Alpine.data('tripEditor', tripEditor);
}

// Global function for opening trip modal (backwards compatibility with trip-card.twig)
window.openTripModal = function(tripData) {
    window.dispatchEvent(new CustomEvent('open-trip-modal', { detail: tripData }));
};

// Global function for deleting a trip via the modal
window.deleteTripModal = function(tripData) {
    window.dispatchEvent(new CustomEvent('delete-trip-modal', { detail: tripData }));
};

// Export for testing and direct usage
export default tripEditor;
